/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import { IconButton, Menu, MenuItem, Divider, Tooltip } from "@mui/material";
import { TaskModel, TaskStatus } from "../../Models/Models";
import styles from "../../Styles/tasksDashboard.module.css";
import { useEffect, useState } from "react";
import { Vocabulary } from "../../Utils/Vocabulary";
import VehicleInfoCell from "../Generic/VehicleInfoCell";
import {
  TASK_PRIORITY_STYLES,
  TASK_STATUS_COLUMN_CONFIG,
} from "../../Utils/tasksConstants";

type Props = {
  task: TaskModel;
  onEdit: (task: TaskModel) => void;
  onDelete: (task: TaskModel) => void;
  onStatusChange?: (task: TaskModel, status: TaskStatus) => void;
  mobile?: boolean;
  isNew?: boolean;
};

function formatDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  const datePart = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  if (!hasTime) return datePart;
  const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${datePart} ${timePart}`;
}

function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}


type TimingInfo = {
  label: string;
  urgency: "error" | "warning" | "default";
  icon: React.ReactElement;
  tooltip: string;
};

function getTimingInfo(task: TaskModel): TimingInfo | null {
  if (!task.estimated_duration) return null;

  const isCompleted = task.status === "completed";

  if (isCompleted) {
    if (task.is_late && task.time_spent_minutes != null) {
      const lateMin = task.time_spent_minutes - task.estimated_duration;
      return {
        label: `${formatMinutes(Math.max(0, lateMin))} ${Vocabulary.tasksTimeOverdue}`,
        urgency: "error",
        icon: <HistoryIcon style={{ fontSize: 12 }} />,
        tooltip: Vocabulary.tasksOverdueLateCompletion,
      };
    }
    return null;
  }

  if (!task.started_at) {
    if (!task.start_date) {
      return {
        label: formatMinutes(task.estimated_duration),
        urgency: "default",
        icon: <AccessTimeIcon style={{ fontSize: 12 }} />,
        tooltip: Vocabulary.taskEstimatedDuration,
      };
    }
    const now = new Date();
    const startDate = new Date(task.start_date);
    if (startDate > now) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const diffDays = Math.round((startDay.getTime() - today.getTime()) / 86_400_000);
      const label =
        diffDays === 0 ? Vocabulary.tasksDueToday :
        diffDays === 1 ? Vocabulary.tasksDueTomorrow :
        `${Vocabulary.tasksStartsIn} ${diffDays}z`;
      return {
        label,
        urgency: "default",
        icon: <CalendarTodayIcon style={{ fontSize: 12 }} />,
        tooltip: formatDate(task.start_date) ?? "",
      };
    }
  }

  if (task.is_late) {
    const spentMin = task.time_spent_minutes ?? 0;
    const overdueMin = Math.max(0, spentMin - task.estimated_duration);
    return {
      label: `${formatMinutes(overdueMin)} ${Vocabulary.tasksTimeOverdue}`,
      urgency: "error",
      icon: <ErrorOutlineIcon style={{ fontSize: 12 }} />,
      tooltip: Vocabulary.tasksOverdue,
    };
  }

  const spentMin = task.time_spent_minutes ?? 0;
  const remainMin = Math.max(0, task.estimated_duration - spentMin);
  const pctLeft = remainMin / task.estimated_duration;
  return {
    label: `${formatMinutes(remainMin)} ${Vocabulary.tasksTimeLeft}`,
    urgency: pctLeft < 0.25 ? "error" : pctLeft < 0.5 ? "warning" : "default",
    icon: pctLeft < 0.5
      ? <AccessAlarmIcon style={{ fontSize: 12 }} />
      : <AccessTimeIcon style={{ fontSize: 12 }} />,
    tooltip: "",
  };
}

function formatLive(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m`;
}

export function LiveTimingChip({ task }: { task: TaskModel }) {
  const [elapsed, setElapsed] = useState(() =>
    task.started_at
      ? Math.floor((Date.now() - new Date(task.started_at).getTime()) / 1000)
      : 0
  );

  useEffect(() => {
    if (!task.started_at) {
      setElapsed(0);
      return;
    }
    const startMs = new Date(task.started_at).getTime();
    setElapsed(Math.floor((Date.now() - startMs) / 1000));
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - startMs) / 1000)),
      30_000
    );
    return () => clearInterval(id);
  }, [task.started_at]);

  if (!task.estimated_duration) return null;

  const estimatedSec = task.estimated_duration * 60;
  const isOverdue = elapsed >= estimatedSec;
  const remaining = estimatedSec - elapsed;
  const pctLeft = remaining / estimatedSec;

  if (isOverdue) {
    return (
      <Tooltip title={Vocabulary.tasksOverdue} placement="top" arrow>
        <span className={`${styles.cardTiming} ${styles.cardTimingError}`}>
          <ErrorOutlineIcon style={{ fontSize: 12 }} />
          {formatLive(elapsed - estimatedSec)} {Vocabulary.tasksTimeOverdue}
        </span>
      </Tooltip>
    );
  }

  return (
    <span className={`${styles.cardTiming} ${pctLeft < 0.25 ? styles.cardTimingError : pctLeft < 0.5 ? styles.cardTimingWarning : ""}`}>
      {pctLeft < 0.5
        ? <AccessAlarmIcon style={{ fontSize: 12 }} />
        : <AccessTimeIcon style={{ fontSize: 12 }} />}
      {formatLive(remaining)} {Vocabulary.tasksTimeLeft}
    </span>
  );
}

function hasLateStart(task: TaskModel): boolean {
  return !!task.started_at && !!task.start_date &&
    new Date(task.started_at) > new Date(task.start_date);
}

type CardBodyProps = {
  task: TaskModel;
  onEdit?: (task: TaskModel) => void;
  onDelete?: (task: TaskModel) => void;
  onStatusChange?: (task: TaskModel, status: TaskStatus) => void;
  mobile?: boolean;
  isOverlay?: boolean;
};

function TaskCardBody({ task, onEdit, onDelete, onStatusChange, mobile, isOverlay }: CardBodyProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const otherStatuses = TASK_STATUS_COLUMN_CONFIG.filter((c) => c.status !== task.status);
  const isLive = task.status === "in_progress" && !!task.started_at;
  const timingInfo = isLive ? null : getTimingInfo(task);
  const lateStart = hasLateStart(task);
  const priorityStyle = task.priority ? TASK_PRIORITY_STYLES[task.priority] : null;

  function openMenu(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  }

  function closeMenu() {
    setAnchorEl(null);
  }

  return (
    <>
      {!isOverlay && (
        <div className={styles.cardActions}>
          <IconButton
            size="small"
            onClick={openMenu}
            onPointerDown={(e) => e.stopPropagation()}
            className={styles.menuButton}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => { closeMenu(); onEdit?.(task); }}
              onPointerDown={(e) => e.stopPropagation()}
              dense
            >
              <EditIcon fontSize="small" style={{ marginRight: 8, color: "#555" }} />
              {Vocabulary.edit}
            </MenuItem>
            <MenuItem
              onClick={() => { closeMenu(); onDelete?.(task); }}
              onPointerDown={(e) => e.stopPropagation()}
              dense
            >
              <DeleteIcon fontSize="small" style={{ marginRight: 8, color: "#e74c3c" }} />
              <span style={{ color: "#e74c3c" }}>{Vocabulary.delete}</span>
            </MenuItem>

            {mobile && onStatusChange && otherStatuses.length > 0 && (
              <div>
                <Divider />
                {otherStatuses.map((col) => (
                  <MenuItem
                    key={col.status}
                    dense
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => { closeMenu(); onStatusChange(task, col.status); }}
                  >
                    <ArrowForwardIcon fontSize="small" style={{ marginRight: 8, color: col.accentColor }} />
                    <span style={{ color: col.color }}>{col.label}</span>
                  </MenuItem>
                ))}
              </div>
            )}
          </Menu>
        </div>
      )}

      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>
          {task.name}
          {!isOverlay && (
            <EditIcon
              className={styles.titleEditIcon}
              onClick={(e) => { e.stopPropagation(); onEdit?.(task); }}
              onPointerDown={(e) => e.stopPropagation()}
            />
          )}
        </span>
        {priorityStyle && (
          <span className={`${styles.cardPriorityChip} ${styles[priorityStyle.chipClass as keyof typeof styles]}`}>
            {priorityStyle.label}
          </span>
        )}
      </div>

      {task.description ? (
        <p className={styles.cardDescription}>{task.description}</p>
      ) : null}

      {task.vehicle_info && (
        <VehicleInfoCell
          name={task.vehicle_info.name}
          vin={task.vehicle_info.vin}
          compact
        />
      )}

      <div className={styles.cardMeta}>
        <div className={styles.cardMetaLeft}>
          {isLive && <LiveTimingChip task={task} />}
          {!isLive && timingInfo && (
            <Tooltip
              title={timingInfo.tooltip}
              placement="top"
              arrow
              disableHoverListener={!timingInfo.tooltip}
            >
              <span className={`${styles.cardTiming} ${
                timingInfo.urgency === "error" ? styles.cardTimingError :
                timingInfo.urgency === "warning" ? styles.cardTimingWarning : ""
              }`}>
                {timingInfo.icon}
                {timingInfo.label}
              </span>
            </Tooltip>
          )}
          {lateStart && (
            <Tooltip title={Vocabulary.tasksOverdueLateStart} placement="top" arrow>
              <span className={`${styles.cardTiming} ${styles.cardTimingWarning}`}>
                <HistoryIcon style={{ fontSize: 12 }} />
                {Vocabulary.tasksOverdueLateStart}
              </span>
            </Tooltip>
          )}
        </div>

        {task.requires_document != false && (
          task.document_path ? (
            <div
              className={`${styles.cardDocumentBadge} ${styles.cardDocumentBadgeUploaded}`}
              data-file={`/api/${task.document_path}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              title={Vocabulary.taskDocUploaded}
            >
              <AttachFileIcon style={{ fontSize: 12 }} />
              <span>{Vocabulary.taskDocUploaded}</span>
            </div>
          ) : (
            <div className={styles.cardDocumentBadge}>
              <AttachFileIcon style={{ fontSize: 12 }} />
              <span>{Vocabulary.taskDocRequired}</span>
            </div>
          )
        )}
      </div>
    </>
  );
}

export function TaskCardOverlay({ task }: { task: TaskModel }) {
  return (
    <div className={`${styles.card} ${styles.cardOverlay}`}>
      <TaskCardBody task={task} isOverlay />
    </div>
  );
}

export default function TaskCard({ task, onEdit, onDelete, onStatusChange, mobile, isNew }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id! });

  const style = {
    transform: mobile ? undefined : CSS.Transform.toString(transform),
    transition: mobile ? undefined : transition,
  };

  const dragProps = mobile
    ? { style: { ...style, cursor: "pointer" as const }, onClick: () => onEdit(task) }
    : { ref: setNodeRef, style, ...listeners, ...attributes, onClick: () => onEdit(task) };

  return (
    <div
      {...dragProps}
      className={`${styles.card} ${isDragging ? styles.cardGhost : ""} ${isNew ? styles.cardSlideIn : ""}`}
    >
      {!isDragging && (
        <TaskCardBody
          task={task}
          onEdit={onEdit}
          onDelete={onDelete}
          onStatusChange={onStatusChange}
          mobile={mobile}
        />
      )}
    </div>
  );
}
